import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { antonio } from '@/lib/fonts';
import styles from './golden-duel-rules-popup.module.scss';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { BasicModalHeader, ScrollableModal } from '@/components/widgets/scrollable-modal'
import { NBATag } from '../nba-tag/nba-tag';
import { goldenDuelModalService } from '@/services/golden-duel/golden-duel-modal.service';
import { useParams, useSearchParams } from 'next/navigation';
import { useMatchGroups } from '@/hooks/query/golden-duel/use-golden-duel-match-group';
import { useAccountInfo } from '@/hooks/query/account';
import { UserInfo } from '@nf/types/account';
import { useGoldenDuelNavigation } from '@/hooks/golden-duel';
import { siteService } from '@/services/site.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { ApiSiteNameEnums } from '@nf/types/site';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { useLocale } from 'next-intl';
import { CdnImg } from '@/features/cdn-img';
import { AutoPopupKey, useRegisterAutoPopup } from '@nf/auto-popup-handler';

type TabType = 'howtoplay' | 'moredetails';
type FeedbackType = 'like' | 'dislike' | null;

export const GoldenDuelRulesPopup = () => {
	const t = useTranslations('General');
	const [activeTab, setActiveTab] = useState<TabType>('howtoplay');
	const [feedbackStatus, setFeedbackStatus] = useState<FeedbackType>(null);
	const [isShowRules, setIsShowRules] = useState(false);
	const canShow = useRegisterAutoPopup({
		key: AutoPopupKey.GoldenDuelRulesPopup,
		isEnabled: isShowRules,
	});
	const searchParams = useSearchParams();

	const { data: matchGroups } = useMatchGroups(
		{ filterType: 0, drawTime: '' }, 
		{ enabled: isShowRules }
	);
	
	const hasAvailableMatch = matchGroups?.Data?.[0]?.Matches?.some(
		match => match.HomeFtScore === undefined || match.HomeFtScore === null
	) ?? false;

	const { user } = useAccountInfo();
	const { navigateToFirstUndrawnMatch } = useGoldenDuelNavigation(isShowRules);
	const locale = useLocale();
	const isUseNewFont = !['th-TH', 'vi-VN'].includes(locale);
	const params = useParams();
	const isSportsRoute = params.sport && params.leagueId && params.matchId;
	
	useEffect(() => {
		const subscription = goldenDuelModalService.goldenDuelRulesModal$.subscribe((isShow) => {
			setIsShowRules(isShow);
		});
		return () => subscription.unsubscribe();
	}, [searchParams])

	const closeRulesPopup = () => {
		setFeedbackStatus(null);
		goldenDuelModalService.displayGoldenDuelRulesModal(false);
	}

	const handleTabClick = (tab: TabType) => () => {
		setFeedbackStatus(null);
		setActiveTab(tab);
		const action = tab === 'howtoplay' ? 13 : 17;
		setKafka(action);
	}

	const handleFeedbackClick = (type: FeedbackType) => () => {
		setFeedbackStatus(type);
		let action = 0;
		if (activeTab === 'howtoplay') {
			action = type === 'like' ? 14 : 15;
		} else if (activeTab === 'moredetails') {
			action = type === 'like' ? 18 : 19;
		}
		setKafka(action);
	}

	const handleClickPlayNow = async () => {
		setKafka(activeTab === 'howtoplay' ? 16 : 20);
		if (!isSportsRoute) await navigateToFirstUndrawnMatch();
		closeRulesPopup();
	}

	const tabItems = [
		{
			label: t('lbl_RRHowToPlay'),
			value: 'howtoplay'
		},
		{
			label: t('lbl_RRMoreDetails'),
			value: 'moredetails'
		}
	]

	const setKafka = (action: number) => {
		const kafkaData = {
			note: KafkaNote.GoldenDuel,
			type: 1,
			action
		}
		setKafkaNormal(kafkaData);
	}
	return (
		<ScrollableModal
			isOpen={canShow}
			closeModal={closeRulesPopup}
			className={styles.modalContainer}
			header={(
				<>
					<BasicModalHeader
						className={clsx(styles.header, isUseNewFont && antonio.className)}
						title={t('lbl_RR')}
						closeModal={closeRulesPopup}
					/>
					<div className={clsx(styles.tabContainer, isUseNewFont && antonio.className)}>
						<div className={styles.tabWrapper}>
							{tabItems.map((item) => (
								<div
									className={styles.tabItem}
									data-selected={activeTab === item.value}
									onClick={handleTabClick(item.value as TabType)}
									key={item.value}
								>
									{item.label}
								</div>
							))}
						</div>
					</div>
				</>
			)}
			footer={
				hasAvailableMatch ? (
					<div className={styles.footer}>
						<button
							className={styles.betButton}
							onClick={handleClickPlayNow}
						>
							<span className={clsx(isUseNewFont && antonio.className)}>{t('lbl_PlayNow')}</span>
							<Icon name="arrow-right" />
						</button>
					</div>
				) : null
			}
		>
			<section className={styles.rulesContent} data-has-match={hasAvailableMatch}>
				{activeTab === 'howtoplay' ? (
					<HowToPlayContent user={user} />
				) : (
					<MoreDetailsContent />
				)}
				<div className={styles.feedbackContainer}>
					<div className={styles.feedbackTitle}>{t('lbl_RRArticleHelpful')}</div>
					<div className={styles.feedbackButtons}>
						<Button
							variant="icon"
							className={styles.feedbackButton}
							data-selected={feedbackStatus === 'like'}
							onClick={handleFeedbackClick('like')}
						>
							<Icon name="thumb-up" />
						</Button>
						<Button
							variant="icon"
							className={styles.feedbackButton}
							data-selected={feedbackStatus === 'dislike'}
							onClick={handleFeedbackClick('dislike')}
						>
							<Icon name="thumb-down" />
						</Button>
					</div>
				</div>
			</section>
		</ScrollableModal>
	)
};


const HowToPlayContent = ({ user }: { user: UserInfo | null | undefined }) => {
	const t = useTranslations('General');
	return (
		<div className={styles.stepContainer}>
			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>1</span>
					<span className={styles.stepText}>{t('lbl_RRHowToPlay1')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_RRHowToPlay1Desc')}
				</div>
				<div className={styles.stepImage}>
					<NBATag size={'small'} className={styles.nbaTag} />
					<CdnImg
						isGalaxyFolder={true}
						src={'/images/promotion/golden-duel/rules-popup/howtoplay-step1.png'}
						width={280}
						height={160}
					/>
				</div>
			</div>

			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>2</span>
					<span className={styles.stepText}>{t('lbl_RRHowToPlay2')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_RRHowToPlay2Desc')}
				</div>
				<div className={styles.stepImageContainer}>
					<div className={styles.stepImage}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/howtoplay-step2.png'}
							width={280}
							height={160}
						/>
						<div className={styles.betText}>{t('lbl_bet')}</div>
					</div>
					<div className={styles.stepImageText}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/my-tickets/ac-nba.svg'}
							width={28}
							height={18.7}
						/>
						<div dangerouslySetInnerHTML={{
							__html: t.raw('lbl_AmountShort')
								.replace(/\{currency\} \{x\}/, `<span class="${styles.highlightNumber}">${user?.Curr || ''} 2</span>`)
						}}
						/>
					</div>
				</div>
			</div>

			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>3</span>
					<span className={styles.stepText}>{t('lbl_RRHowToPlay3')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_RRHowToPlay3Desc')}
				</div>
				<div className={styles.stepImage}>
					<CdnImg
						isGalaxyFolder={true}
						src={'/images/promotion/golden-duel/rules-popup/howtoplay-step3.png'}
						width={280}
						height={160}
					/>
				</div>
			</div>
		</div>
	);
};

const MoreDetailsContent = () => {
	const t = useTranslations('General');
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const isTgxi = siteService.isMatchingSite(siteConfig?.GetMessageID, [ApiSiteNameEnums.TGXI, ApiSiteNameEnums.TGXISW]);
	const locale = useLocale();
	const isUseNewFont = !['th-TH', 'vi-VN'].includes(locale);

	return (
		<>
			<div className={styles.rulesTitle}>
				<span className={clsx(isUseNewFont && antonio.className)}>{t('lbl_RRMoreDetails01-1')}</span>
				<span> {t('lbl_RRMoreDetails01-2')}</span>
			</div>
			<div className={styles.rulesDescription}>
				{isTgxi ? t('lbl_RRMoreDetails37') : t('lbl_RRMoreDetails02')}
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails03')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails04')}</li>
					<li>{t('lbl_RRMoreDetails05')}</li>
					<li>{t('lbl_RRMoreDetails06')}</li>
					<li>{t('lbl_RRMoreDetails07')}</li>
					<li>{isTgxi ? t('lbl_RRMoreDetails38') : t('lbl_RRMoreDetails08')}</li>
					<li>{t('lbl_RRMoreDetails09')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails10')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails11')}</li>
					<li>{t('lbl_RRMoreDetails12')}</li>
					<li>{t('lbl_RRMoreDetails13')}</li>
					<li>{isTgxi ? t('lbl_RRMoreDetails39') : t('lbl_RRMoreDetails14')}</li>
					<li>{t('lbl_RRMoreDetails15')}</li>
					<li>{t('lbl_RRMoreDetails16')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails44')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails45')}</li>
					<li>{t('lbl_RRMoreDetails46')}</li>
					<li>{t('lbl_RRMoreDetails47')}</li>
					<li>{t('lbl_RRMoreDetails48')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails17')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails18')}</li>
					<li>{t('lbl_RRMoreDetails19')}</li>
					<li>{t('lbl_RRMoreDetails20')}</li>
					<li>{t('lbl_RRMoreDetails21')}</li>
					<li>{isTgxi ? t('lbl_RRMoreDetails40') : t('lbl_RRMoreDetails22')}</li>
					<li>{isTgxi ? t('lbl_RRMoreDetails41') : t('lbl_RRMoreDetails23')}</li>
					<li>{t('lbl_RRMoreDetails24')}</li>
					<li>{isTgxi ? t('lbl_RRMoreDetails42') : t('lbl_RRMoreDetails25')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails26')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails27')}</li>
					<li>{t('lbl_RRMoreDetails28')}</li>
					<li>{t('lbl_RRMoreDetails29')}</li>
					<li>{t('lbl_RRMoreDetails30')}</li>
					{isTgxi ? <li>{t('lbl_RRMoreDetails43')}</li> : null}
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_RRMoreDetails31')}</div>
				<ul>
					<li>{t('lbl_RRMoreDetails32')}</li>
					<li>{t('lbl_RRMoreDetails33')}</li>
					<li>{t('lbl_RRMoreDetails34')}</li>
					<li>{t('lbl_RRMoreDetails35')}</li>
					<li>{t('lbl_RRMoreDetails36')}</li>
				</ul>
			</div>

			<div className={styles.lotteryRulesContainer}>
				<div className={styles.lotteryTitleContainer}>
					<div className={styles.lotteryTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-basketball.svg'}
							width={16}
							height={16}
						/>

						<span className={clsx(isUseNewFont && antonio.className, styles.noWrap)}>{t('lbl_RRMoreDetails01-1')}</span>
						<span> {t('lbl_RRDrawRules01-2')}</span>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-basketball.svg'}
							width={16}
							height={16}
						/>
					</div>
					<div className={styles.lotterySubtitle}>{t('lbl_RRDrawRules02')}</div>
				</div>
				<div className={styles.lotterySectionDesc}>
					{t('lbl_RRDrawRules03')}
				</div>
				<div className={styles.lotterySection}>
					<div className={styles.lotterySectionTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-flat.svg'}
							width={16}
							height={16}
						/>
						<div dangerouslySetInnerHTML={{
							__html: t.raw('lbl_RRDrawRules04')
						}}
						/>
					</div>
					<div className={styles.lotterySectionDesc}>
						{t('lbl_RRDrawRules05')}
					</div>
					<ul>
						<li>{t('lbl_RRDrawRules06')}</li>
						<li>{t('lbl_RRDrawRules07')}</li>
						<li>{t('lbl_RRDrawRules08')}</li>
						<li>{t('lbl_RRDrawRules09')}</li>
						<li>{t('lbl_RRDrawRules10')}</li>
						<li>{t('lbl_RRDrawRules11')}</li>
					</ul>
				</div>

				<div className={styles.lotterySection}>
					<div className={styles.lotterySectionTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-flat.svg'}
							width={16}
							height={16}
						/>
						<div dangerouslySetInnerHTML={{
							__html: t.raw('lbl_RRDrawRules12')
						}}
						/>
					</div>
					<div className={styles.lotterySectionDesc}>
						{t('lbl_RRDrawRules13')}
					</div>
					<ul>
						<li>{t('lbl_RRDrawRules06')}</li>
						<li>{t('lbl_RRDrawRules07')}</li>
						<li>{t('lbl_RRDrawRules08')}</li>
						<li>{t('lbl_RRDrawRules14')}</li>
						<li>{t('lbl_RRDrawRules15')}</li>
						<li>{t('lbl_RRDrawRules16')}</li>
					</ul>
				</div>

				<div className={styles.lotterySection}>
					<div className={styles.lotterySectionTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-flat.svg'}
							width={16}
							height={16}
						/>
						<div dangerouslySetInnerHTML={{
							__html: t.raw('lbl_RRDrawRules17')
						}}
						/>
					</div>
					<div className={styles.lotterySectionDesc}>
						{t('lbl_RRDrawRules18')}
					</div>

					<ul>
						<li>{t('lbl_RRDrawRules19')}</li>
						<li>{t('lbl_RRDrawRules20')}</li>
						<li>{t('lbl_RRDrawRules21')}</li>
						<li>{t('lbl_RRDrawRules22')}</li>
						<li>{t('lbl_RRDrawRules23')}</li>
						<li>{t('lbl_RRDrawRules24')}</li>
					</ul>
				</div>

				<div className={styles.lotterySection}>
					<div className={styles.lotterySectionTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-flat.svg'}
							width={16}
							height={16}
						/>
						<div dangerouslySetInnerHTML={{
							__html: t.raw('lbl_RRDrawRules25')
						}}
						/>
					</div>
					<div className={styles.lotterySectionDesc}>
						{t('lbl_RRDrawRules26')}
					</div>
					<ul>
						<li>{t('lbl_RRDrawRules19')}</li>
						<li>{t('lbl_RRDrawRules27')}</li>
						<li>{t('lbl_RRDrawRules28')}</li>
						<li>{t('lbl_RRDrawRules29')}</li>
					</ul>
				</div>

				<div className={styles.lotterySection}>
					<div className={styles.lotterySectionTitle}>
						<CdnImg
							isGalaxyFolder={true}
							src={'/images/promotion/golden-duel/rules-popup/icon-megaphone.svg'}
							width={16}
							height={16}
						/>
						{t('lbl_RRDrawRules30')}
					</div>
					<div className={styles.lotterySectionDesc}>
						{t('lbl_RRDrawRules31')}
						{t('lbl_RRDrawRules32')}
						{t('lbl_RRDrawRules33')}
					</div>

					<div className={styles.lotterySectionHighlightDesc}>
						{t('lbl_RRDrawRules34')}
					</div>
				</div>
			</div>
		</>
	);
};